<template>
  <Transition name="toast">
    <div
      v-if="visible"
      :class="[
        'fixed top-4 right-0 left-0 mx-auto z-50 max-w-md w-full  shadow-xl rounded-xl p-4 flex items-start gap-3',
        variantClasses
      ]"
      role="alert"
    >
      <!-- Icon -->
      <component :is="icon" :size="20" :class="iconClasses" />

      <!-- Content -->
      <div class="flex-1 min-w-0">
        <h4 v-if="title" :class="titleClasses">
          {{ title }}
        </h4>
        <p :class="messageClasses">
          {{ message }}
        </p>
      </div>

      <!-- Close Button -->
      <button
        @click="handleClose"
        :class="closeButtonClasses"
        aria-label="Close notification"
      >
        <X :size="16" />
      </button>
    </div>
  </Transition>
</template>

<script>
import { X, AlertCircle, CheckCircle, Info, AlertTriangle } from "lucide-vue";

export default {
  name: "Toast",
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ""
    },
    message: {
      type: String,
      required: true
    },
    variant: {
      type: String,
      default: "info",
      validator: (value) =>
        ["info", "success", "warning", "danger"].includes(value)
    },
    duration: {
      type: Number,
      default: 5000
    }
  },
  data() {
    return {
      timeoutId: null
    };
  },
  components: {
    X,
    AlertCircle,
    CheckCircle,
    Info,
    AlertTriangle
  },
  computed: {
    variantClasses() {
      const variants = {
        info: "bg-blue-50 border border-blue-200",
        success: "bg-green-50 border border-green-200",
        warning: "bg-yellow-50 border border-yellow-200",
        danger: "bg-red-50 border border-red-200"
      };
      return variants[this.variant] || variants.info;
    },
    icon() {
      const icons = {
        info: Info,
        success: CheckCircle,
        warning: AlertTriangle,
        danger: AlertCircle
      };
      return icons[this.variant] || Info;
    },
    iconClasses() {
      const classes = {
        info: "text-blue-600 flex-shrink-0 mt-0.5",
        success: "text-green-600 flex-shrink-0 mt-0.5",
        warning: "text-yellow-600 flex-shrink-0 mt-0.5",
        danger: "text-red-600 flex-shrink-0 mt-0.5"
      };
      return classes[this.variant] || classes.info;
    },
    titleClasses() {
      const classes = {
        info: "text-blue-900 font-bold text-sm mb-1",
        success: "text-green-900 font-bold text-sm mb-1",
        warning: "text-yellow-900 font-bold text-sm mb-1",
        danger: "text-red-900 font-bold text-sm mb-1"
      };
      return classes[this.variant] || classes.info;
    },
    messageClasses() {
      const classes = {
        info: "text-blue-800 text-sm",
        success: "text-green-800 text-sm",
        warning: "text-yellow-800 text-sm",
        danger: "text-red-800 text-sm"
      };
      return classes[this.variant] || classes.info;
    },
    closeButtonClasses() {
      const classes = {
        info:
          "text-blue-400 hover:text-blue-600 transition-colors flex-shrink-0",
        success:
          "text-green-400 hover:text-green-600 transition-colors flex-shrink-0",
        warning:
          "text-yellow-400 hover:text-yellow-600 transition-colors flex-shrink-0",
        danger:
          "text-red-400 hover:text-red-600 transition-colors flex-shrink-0"
      };
      return classes[this.variant] || classes.info;
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.startTimer();
      } else {
        this.clearTimer();
      }
    }
  },
  mounted() {
    if (this.visible) {
      this.startTimer();
    }
  },
  beforeDestroy() {
    this.clearTimer();
  },
  methods: {
    handleClose() {
      this.$emit("close");
    },
    startTimer() {
      this.clearTimer();
      if (this.duration > 0) {
        this.timeoutId = setTimeout(() => {
          this.handleClose();
        }, this.duration);
      }
    },
    clearTimer() {
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
    }
  }
};
</script>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter {
  opacity: 0;
  transform: translateX(100%);
}

.toast-enter-to {
  opacity: 1;
  transform: translateX(0);
}

.toast-leave {
  opacity: 1;
  transform: translateX(0);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}
</style>
